/**
 * Hook that wraps the `deleteJCode` POST call with abort support and
 * `onSuccess` / `onFailure` callbacks while exposing a loading flag for
 * the calling UI.
 *
 * @module useDeleteJCode
 */

import { useCallback, useRef, useState } from "react";
import {
  deleteJCode,
  DeleteJCodePayload,
} from "../../../../../api/postCall/deleteJCode";
import { useAbortableFetch } from "../../../../../hooks/useAbortableFetch";

/** Configuration callbacks accepted by {@link useDeleteJCode}. */
interface UseDeleteJCodeOptions {
  /**
   * Called after the request resolves successfully. Receives the parsed
   * server response and the payload that was deleted. May be async — the
   * returned promise is awaited and the hook's `loading` flag stays
   * `true` until it settles.
   */
  onSuccess?: (
    response: unknown,
    payload: DeleteJCodePayload,
  ) => void | Promise<void>;
  /**
   * Called when the request fails for any non-abort reason. AbortError
   * is intentionally swallowed so callers don't have to dedupe it. May
   * be async — the returned promise is awaited and the hook's `loading`
   * flag stays `true` until it settles.
   */
  onFailure?: (
    error: unknown,
    payload: DeleteJCodePayload,
  ) => void | Promise<void>;
}

/**
 * Build the abort-aware fetcher that `useAbortableFetch` will invoke.
 * Kept module-level (instead of inline) so its reference is stable
 * across renders without needing `useCallback` / `useRef` plumbing.
 */
const deleteJCodeFetchFn = (signal: AbortSignal, payload: DeleteJCodePayload) =>
  deleteJCode(payload, signal);

/**
 * React hook for deleting a single J-code from an order.
 *
 * Wires `deleteJCode` through `useAbortableFetch` so the in-flight
 * request is automatically cancelled when a new delete is triggered or
 * when the consumer calls the returned `abort`. Forwards the resolved
 * response or thrown error to the caller-supplied callbacks.
 *
 * @param options - Optional `onSuccess` / `onFailure` callbacks. Both
 *   are read from a ref so updating them between renders does not
 *   invalidate the returned `triggerDelete` reference.
 * @returns
 * - `triggerDelete(payload)` — issue the delete and resolve with the
 *   shared `useAbortableFetch` result shape (`{ success, data, error }`).
 * - `abort()` — cancel the in-flight delete (no-op if none).
 * - `loading` — `true` while a delete is in flight.
 * - `data` — last successful response (null until a success).
 * - `error` — last non-abort error (null on success / abort).
 */
export function useDeleteJCode(options: UseDeleteJCodeOptions = {}) {
  const callbacksRef = useRef(options);
  callbacksRef.current = options;

  const { runFetch, abort, loading, data, error } =
    useAbortableFetch<unknown>(deleteJCodeFetchFn);

  // Tracks the async callback phase that runs after the network request
  // resolves. Merged into the public `loading` flag so consumers see a
  // single boolean covering both phases.
  const [callbackPending, setCallbackPending] = useState(false);

  const triggerDelete = useCallback(
    async (payload: DeleteJCodePayload) => {
      const result = await runFetch(payload);

      const callback = result.success
        ? callbacksRef.current.onSuccess
        : result.error?.name !== "AbortError"
          ? callbacksRef.current.onFailure
          : undefined;

      if (!callback) {
        return result;
      }

      setCallbackPending(true);
      try {
        await callback(result.success ? result.data : result.error, payload);
      } finally {
        setCallbackPending(false);
      }
      return result;
    },
    [runFetch],
  );

  return {
    triggerDelete,
    abort,
    loading: loading || callbackPending,
    data,
    error,
  };
}
